// 縮圖（plan/SPEC.md vms-web「縮圖」）：façade GET …/vms/devices/{d}/thumbnail?at=——雲端片段一張，第一次有人看才抽。
// 這裡做用戶端快取（同一個片段的時間對到同一張：以 10 秒為一格）與失敗的短期記憶（沒錄影 / 這個環境沒開，不一直重問）。
import { useEffect, useRef, useState } from 'react';
import { KinioError, kinioFetchResponse, localThumb, planeBase } from '@kinio/web-sdk';

const BUCKET_MS = 10_000;
const MAX = 300;
const NEG_TTL_MS = 5 * 60_000;

type Entry = { url: string } | { failed: string; at: number };
const cache = new Map<string, Entry>();
const inflight = new Map<string, Promise<string | null>>();
/** 這個 plane 的縮圖沒開（501）：整頁不再問。 */
const disabled = new Set<string>();

// 多鏡頭（plan SPEC #multi-lens-recording）：lens ≠ 0 帶 `&lens=`（副鏡頭的片段抽圖），快取鍵也分開；0 = 主鏡頭照舊。
export function thumbKey(homeId: string, device: string, atMs: number, lens = 0): string {
  return `${homeId}/${device}${lens ? `#${lens}` : ''}@${Math.floor(atMs / BUCKET_MS)}`;
}

export function thumbUrl(api: string, homeId: string, device: string, atMs: number, lens = 0): string {
  return `${planeBase(api)}/v1/homes/${encodeURIComponent(homeId)}/vms/devices/${encodeURIComponent(device)}/thumbnail?at=${encodeURIComponent(new Date(atMs).toISOString())}${lens ? `&lens=${lens}` : ''}`;
}

function remember(key: string, e: Entry) {
  cache.set(key, e);
  if (cache.size > MAX) {
    const [old, v] = cache.entries().next().value as [string, Entry];
    cache.delete(old);
    if ('url' in v) URL.revokeObjectURL(v.url);
  }
}

/** 取縮圖的 object URL；沒有（沒錄影 / 還沒上來 / 沒開 / 錯誤）回 null。 */
export function loadThumb(api: string, homeId: string, device: string, atMs: number, now = Date.now(), lens = 0): Promise<string | null> {
  if (disabled.has(api)) return Promise.resolve(null);
  const key = api + '|' + thumbKey(homeId, device, atMs, lens);
  const hit = cache.get(key);
  if (hit && 'url' in hit) return Promise.resolve(hit.url);
  if (hit && now - hit.at < NEG_TTL_MS) return Promise.resolve(null);
  const running = inflight.get(key);
  if (running) return running;
  const p = (async () => {
    try {
      const res = await kinioFetchResponse(thumbUrl(api, homeId, device, atMs, lens));
      const url = URL.createObjectURL(await res.blob());
      remember(key, { url });
      return url;
    } catch (e) {
      const code = e instanceof KinioError ? e.code : 'error';
      if (e instanceof KinioError && e.status === 501) disabled.add(api);
      if (!(e instanceof KinioError && e.status === 425)) remember(key, { failed: code, at: now }); // 425 = 片段還沒上來：下次再問
      return null;
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, p);
  return p;
}

/** 測試用：清掉快取。 */
export function resetThumbs() {
  cache.clear();
  inflight.clear();
  disabled.clear();
}

/**
 * 一張縮圖：enabled 才載（例如捲到畫面內、或滑鼠停住）。回 object URL 或 null。
 * atMs 換了會換圖；卸載後不更新狀態。
 */
export function useThumb(api: string | undefined, homeId: string, device: string, atMs: number | null, enabled = true, lens = 0): string | null {
  return useThumbState(api, homeId, device, atMs, enabled, lens).url;
}

/** 同 useThumb，另外說查完了沒（settled = 這一格問過了；url 還是 null 就是沒有縮圖；第十一輪 B11-08 寫「沒有縮圖」用）。 */
export function useThumbState(
  api: string | undefined,
  homeId: string,
  device: string,
  atMs: number | null,
  enabled = true,
  lens = 0,
): { url: string | null; settled: boolean } {
  const bucket = atMs === null ? null : Math.floor(atMs / BUCKET_MS);
  const [st, setSt] = useState<{ url: string | null; for: string | null }>({ url: null, for: null });
  const want = api && atMs !== null ? `${api}|${homeId}/${device}#${lens}@${bucket}` : null;
  useEffect(() => {
    if (!api || atMs === null || !enabled) return;
    let alive = true;
    const key = `${api}|${homeId}/${device}#${lens}@${bucket}`;
    void loadThumb(api, homeId, device, atMs, Date.now(), lens).then((u) => alive && setSt({ url: u, for: key }));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, homeId, device, bucket, enabled, lens]);
  if (atMs === null) return { url: null, settled: false };
  // 換了一格還沒查完：先沿用上一張（跟原本 useThumb 一樣不閃），但不算查完
  return { url: st.url, settled: st.for === want };
}

// ---- 本地錄影的縮圖（NVR / Station 的 pb.thumb，時間軸拖曳 / 滑過用；SPEC #vms-web-timeline） ----

const localCache = new Map<string, Entry>();
const localInflight = new Map<string, Promise<string | null>>();

/** pb.thumb 拿那一刻最接近的關鍵幀（data URL）；沒錄影 / 設備不支援 / 失敗回 null（5 分鐘內不重問同一格）。 */
export function loadLocalThumb(api: string, homeId: string, device: string, ch: number, atMs: number, now = Date.now()): Promise<string | null> {
  const key = `${api}|${homeId}/${device}/${ch}@${Math.floor(atMs / BUCKET_MS)}`;
  const hit = localCache.get(key);
  if (hit && 'url' in hit) return Promise.resolve(hit.url);
  if (hit && now - hit.at < NEG_TTL_MS) return Promise.resolve(null);
  const running = localInflight.get(key);
  if (running) return running;
  const p = localThumb(api, homeId, device, ch, atMs / 1000)
    .then((url) => {
      localCache.set(key, { url });
      if (localCache.size > MAX) localCache.delete(localCache.keys().next().value as string);
      return url;
    })
    .catch((e) => {
      localCache.set(key, { failed: e instanceof KinioError ? e.code : 'error', at: now });
      return null;
    })
    .finally(() => localInflight.delete(key));
  localInflight.set(key, p);
  return p;
}

export function useLocalThumb(api: string | undefined, homeId: string, device: string, ch: number, atMs: number | null, enabled = true): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!api || atMs === null || !enabled) return;
    let alive = true;
    void loadLocalThumb(api, homeId, device, ch, atMs).then((u) => alive && setUrl(u));
    return () => {
      alive = false;
    };
  }, [api, homeId, device, ch, atMs === null ? null : Math.floor(atMs / BUCKET_MS), enabled]);
  return atMs === null ? null : url;
}

/** 元素進到畫面內才回 true（之後一直是 true）。沒有 IntersectionObserver 的環境直接 true。 */
export function useSeen<T extends Element>(): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [seen, setSeen] = useState(typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    if (seen || !ref.current) return;
    const io = new IntersectionObserver((es) => es.some((e) => e.isIntersecting) && setSeen(true), { rootMargin: '200px' });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [seen]);
  return [ref, seen];
}
